import axios from "../api/axiosInstance";
import {
  isDemoMode,
  readDemoToken,
  readDemoUser,
  writeDemoToken,
  writeDemoUser,
} from "../demo/demoMode";

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function readStoredUser() {
  if (isDemoMode()) return readDemoUser();
  try {
    return JSON.parse(localStorage.getItem("palantir_user") || "{}");
  } catch (_err) {
    return {};
  }
}

export function writeStoredUser(nextUser, { dispatch = true } = {}) {
  const safeUser = isPlainObject(nextUser) ? nextUser : {};

  try {
    if (isDemoMode()) writeDemoUser(safeUser);
    else localStorage.setItem("palantir_user", JSON.stringify(safeUser));
  } catch (_err) {
    // Ignore storage failures and keep the session in memory only.
  }

  if (dispatch && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("palantir-session-updated", { detail: safeUser }));
  }

  return safeUser;
}

export function updateStoredUser(updater, options = {}) {
  const currentUser = readStoredUser();
  const nextUser =
    typeof updater === "function"
      ? updater(currentUser)
      : { ...currentUser, ...(isPlainObject(updater) ? updater : {}) };

  return writeStoredUser(nextUser, options);
}

export function persistAuthSession(authPayload, fallbackUser = {}) {
  const currentStoredUser = readStoredUser();
  const incomingUser = isPlainObject(authPayload?.user)
    ? authPayload.user
    : isPlainObject(fallbackUser)
      ? fallbackUser
      : {};
  const incomingUserId = String(incomingUser?.id || "").trim();
  const currentStoredUserId = String(currentStoredUser?.id || "").trim();
  const isSameUser = Boolean(incomingUserId) && incomingUserId === currentStoredUserId;
  const nextUser = isSameUser ? { ...currentStoredUser, ...incomingUser } : { ...incomingUser };
  const nextToken = String(authPayload?.token || "").trim();

  if (nextToken) {
    if (isDemoMode()) writeDemoToken(nextToken);
    else localStorage.setItem("palantir_token", nextToken);
    axios.defaults.headers.common.Authorization = `Bearer ${nextToken}`;
  }

  return writeStoredUser(nextUser);
}

export function readStoredAuthToken() {
  if (isDemoMode()) return readDemoToken();
  try {
    return String(localStorage.getItem("palantir_token") || "");
  } catch (_error) {
    return "";
  }
}

export async function setActiveBusinessUnitSession(userId, activeBusinessUnitId) {
  if (isDemoMode()) return readStoredUser();

  const requestToken = readStoredAuthToken();
  const requestUserId = String(readStoredUser()?.id || "");
  const sessionChanged = () => Object.assign(new Error("The browser session changed while selecting this business unit."), {
    code: "SESSION_CHANGED",
  });
  if (!requestToken || requestUserId !== String(userId)) throw sessionChanged();

  const response = await axios.patch(`/api/users/${userId}/set-active-business-unit`, {
    active_business_unit_id: activeBusinessUnitId,
  });

  if (readStoredAuthToken() !== requestToken || String(readStoredUser()?.id || "") !== requestUserId) {
    throw sessionChanged();
  }

  return persistAuthSession(response.data, {
    id: userId,
    active_business_unit_id: activeBusinessUnitId,
  });
}

export async function persistCurrentUserPreferences(preferencesPatch) {
  const safePatch = isPlainObject(preferencesPatch) ? preferencesPatch : {};
  if (!Object.keys(safePatch).length) return readStoredUser();

  const currentUser = readStoredUser();
  const currentUserId = currentUser?.id;
  if (!currentUserId) return currentUser;

  const currentPreferences = isPlainObject(currentUser.preferences) ? currentUser.preferences : {};
  const nextPreferences = { ...currentPreferences, ...safePatch };

  const optimisticUser = updateStoredUser((prev) => ({
    ...prev,
    preferences: {
      ...(isPlainObject(prev?.preferences) ? prev.preferences : {}),
      ...safePatch,
    },
  }));

  if (isDemoMode()) return optimisticUser;

  try {
    const response = await axios.patch(`/api/users/${currentUserId}`, {
      preferences: nextPreferences,
    });

    if (!response?.data?.ok || !isPlainObject(response?.data?.user)) {
      return optimisticUser;
    }

    const latestStoredUser = readStoredUser();
    if (String(latestStoredUser?.id || "") !== String(currentUserId)) {
      return latestStoredUser;
    }

    const apiUser = response.data.user;
    const mergedPreferences = {
      ...(isPlainObject(apiUser.preferences) ? apiUser.preferences : {}),
      ...(isPlainObject(latestStoredUser.preferences) ? latestStoredUser.preferences : {}),
    };

    return writeStoredUser({
      ...latestStoredUser,
      ...apiUser,
      // A preferences-only PATCH must never overwrite auth fields:
      // - login stores role as a flat string ("admin") but the PATCH endpoint
      //   returns it as a Sequelize association object ({ id, name, permissions }),
      //   which makes isAdminRole checks fail and collapses the sidebar menus.
      // - permissions likewise are only authoritative from the login response.
      role: latestStoredUser.role,
      permissions: latestStoredUser.permissions,
      preferences: mergedPreferences,
    });
  } catch (error) {
    console.warn("[SessionUser] Could not persist current user preferences to API:", error);
    return optimisticUser;
  }
}
